@@ -0,0 +1,77 @@
<template>
  <v-col>
    <!-- Loading state: Show skeleton cards -->
    <v-row
      v-if="loading && reportOptions.length === 0"
      class="wrap justify-center"
    >
      <v-col
        v-for="i in 6"
        :key="`skeleton-${i}`"
        class="col-auto"
      >
        <v-card width="320px">
          <v-card-text>
            <v-skeleton-loader
              type="article, list-item-two-line"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Loaded state: Show actual cards -->
    <v-row
      v-else
      class="wrap justify-center"
    >
      <!-- Loop through organizations that have pacing data -->
      <v-col
        v-for="report in reportOptions"
        :key="report.key"
        class="text-truncate col-auto"
      >
        <!-- Enhanced card for external users -->
        <enhanced-network-card
          v-if="showEnhancedCard"
          :report="report"
          :tab-configs="tabConfigs"
          :network-data="getNetworkData(report)"
          :loading="loading"
          :report-path="reportPath"
          :visible-columns="visibleColumns"
        />

        <!-- Standard card for internal users -->
        <v-card
          v-else
          width="320px"
        >
          <v-card-text>
            <v-col>
              <v-row>
                <v-col class="text-truncate">
                  <span
                    class="font-weight-bold primary--text cursor-pointer"
                    :data-href="report.href"
                    :title="report.text"
                    @click="selectSearchItem(report)"
                  >{{ report.text }}</span>
                </v-col>
              </v-row>

              <v-row class="mt-0">
                <v-col class="pt-0">
                  <span class="caption font-weight-bold">Billing Code </span>
                  <span class="caption font-weight-medium">{{ report.billingCode }}</span>
                </v-col>
              </v-row>

              <v-row class="mt-0">
                <v-col class="pt-0">
                  <span class="caption font-weight-bold">Network </span>
                  <span class="caption font-weight-medium">{{ report.networkCode }}</span>
                </v-col>
              </v-row>

              <v-row class="mt-0">
                <v-col class="pt-0">
                  <span class="caption font-weight-bold">Team Code</span>
                  <span class="caption font-weight-medium pl-1">{{ report.teamCode === -1 ? 'All' : report.teamCode }}</span>
                </v-col>
              </v-row>

              <v-row>
                <v-col class="pt-0">
                  <v-tooltip
                    bottom
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <span
                        class="caption font-weight-bold"
                        v-bind="attrs"
                        v-on="on"
                      >Team ID</span>
                    </template>

                    <span>Database identifier</span>
                  </v-tooltip>

                  <span class="caption font-weight-medium pl-1">{{ report.teamId === '-1' ? 'All' : report.teamId }}</span>
                </v-col>
              </v-row>
            </v-col>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
export default {
  name: 'ReportList',
  components: {
    EnhancedNetworkCard: () => import('./EnhancedNetworkCard'),
  },
  props: {
    searchText: {
      type: String,
      default: '',
    },
    isExternalUser: {
      type: Boolean,
      default: false,
    },
    tabConfigs: {
      type: Array,
      default: () => [],
    },
    allPacingData: {
      type: Array,
      default: () => [],
    },
    allDiscrepancyData: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    assignedNetworks: {
      type: Array,
      default: () => [],
    },
    currentTabId: {
      type: String,
      default: '',
    },
    reportPath: {
      type: String,
      default: '/reports/pacing',
    },
    /**
     * Array of visible column value strings to pass to EnhancedNetworkCard.
     * When empty, all card properties are shown.
     */
    visibleColumns: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    showEnhancedCard() {
      // Only show enhanced card on default tabs:
      // - 'assigned-networks' (My Clients) for internal users
      // - 'all-networks' (Networks) for external users
      const isInternalUser = !this.isExternalUser
      const defaultTabs = isInternalUser ? ['assigned-networks'] : ['all-networks']

      return defaultTabs.includes(this.currentTabId)
    },
    allReports() {
      // Use assignedNetworks prop (passed from parent component)
      return this.assignedNetworks
    },
    reportOptions() {
      // If no search text, return all reports
      if (!this.searchText || this.searchText.trim() === '') {
        return this.allReports
      }

      const searchLower = this.searchText.toLowerCase().trim()

      // Filter reports by text, billingCode, networkCode, and teamCode
      return this.allReports.filter((report) => {
        const text = (report.text || '').toString().toLowerCase()
        const billingCode = (report.billingCode || '').toString().toLowerCase()
        const networkCode = (report.networkCode || '').toString().toLowerCase()
        const teamCode = (report.teamCode || '').toString().toLowerCase()

        return text.includes(searchLower)
          || billingCode.includes(searchLower)
          || networkCode.includes(searchLower)
          || teamCode.includes(searchLower)
      })
    },
  },
  methods: {
    getNetworkData(report) {
      // Get the appropriate data source based on what's provided
      const allData = this.allPacingData.length > 0 ? this.allPacingData : this.allDiscrepancyData

      if (!allData || allData.length === 0) {
        return []
      }

      // Filter by matching publisherId and teamIds array
      // Line items from API have publisherId and teamIds array (from ad server)
      // Reports have publisherId and teamId for filtering
      return allData.filter((item) => {
        const matchesNetwork = item.publisherId === report.publisherId

        // teamIds is an array from the ad server (already in API response)
        // If report.teamId is '-1' or -1 (string or number), include all items
        // Otherwise, check if the report's teamId exists in the item's teamIds array
        const matchesTeam = report.teamId === '-1' || report.teamId === -1
          || (Array.isArray(item.teamIds) && item.teamIds.includes(report.teamId))

        return matchesNetwork && matchesTeam
      })
    },

    async selectSearchItem(selection) {
      await this.$store.dispatch('tools/selectSearchItem', { $route: this.$route, selection })

      return this.$store.dispatch('tools/renderMainContent', this.$route)
    },
  },
}
</script>
